
import { Component, Prop, Vue } from 'vue-property-decorator';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

import { sharedState } from '../../../../../framework/state';
import { getResource, putResource } from '../../../../../framework/client/resource';
import { getDatabaseOptions, getEnumerationOptions } from '../../../../../framework/service/options';

import TextField from '../../../../../framework/fields/TextField.vue';
import SelectField from '../../../../../framework/fields/SelectField.vue';
import SwitchField from '../../../../../framework/fields/SwitchField.vue';
import IntegerField from '../../../../../framework/fields/IntegerField.vue';

import { PurchaseInvoiceRowMapping } from '../../../../../../common/application/model/PurchaseInvoiceRowMapping';
import { InvoiceRowType } from '../../../../../../common/application/model/enums/InvoiceRowType';
import { VendorContractType } from '../../../../../../common/application/model/enums/VendorContractType';
import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import { VendorType } from '../../../../../../common/application/model/enums/VendorType';
import { InvoicePeriodizationType } from '../../../../../../common/application/model/enums/InvoicePeriodizationType';

import { getVendorOptions } from '../../../../service/application_options_service';
import { errorToast, successToast } from '../../../../service/toast_service';
import PeriodizationTypeSelector from '../../../shared/PeriodizationTypeSelector.vue';
import { PeriodizationModel } from '../../../../../../common/application/model/PeriodizationModel';
import { InvoicePeriodizationOffsetDirection } from '../../../../../../common/application/model/enums/InvoicePeriodizationOffsetDirection';

@Component({
    components: {
        IntegerField,
        SwitchField,
        SelectField,
        TextField,
        ValidationObserver,
        ValidationProvider,
        PeriodizationTypeSelector,
    },
})
export default class EditPurchaseInvoiceRowMapping extends Vue {
    readonly resourceType = ApplicationResource.PURCHASE_INVOICE_ROW_MAPPING;

    @Prop(String) readonly id!: string;

    shared = sharedState;
    local = {
        row: {} as PurchaseInvoiceRowMapping,
        periodizationModel: {
            type: InvoicePeriodizationType.UNSPECIFIED,
            useOffset: false,
            direction: InvoicePeriodizationOffsetDirection.ADD,
            days: 0,
        } as PeriodizationModel,

        vendorTypeOptions: [] as { id: string | undefined; label: string }[],
        vendorOptions: [] as { id: string | undefined; label: string }[],
        invoiceRowTypeOptions: [] as { id: string | undefined; label: string }[],
        vendorContractTypeOptions: [] as { id: string | undefined; label: string }[],
        invoicePeriodizationTypeOptions: [] as { id: string | undefined; label: string }[],
    };

    onPeriodizationModelChanged(newModel: PeriodizationModel) {
        this.local.periodizationModel = newModel;
        this.local.row = {
            ...this.local.row,
            invoicePeriodizationType: newModel.type,
            invoicePeriodizationUseOffset: newModel.useOffset,
            invoicePeriodizationOffsetDirection: newModel.direction,
            invoicePeriodizationOffsetDays: newModel.days,
        };
    }

    validatePeriodization(value: any) {
        if (!value || !value.type || value.type.toLowerCase() === 'unspecified') {
            return this.$t('message.invoicePeriodizationTypeRequired');
        }
        if (value.useOffset) {
            if (!value.direction) {
                return this.$t('message.offsetDirectionRequired');
            }
            if (!value.days || isNaN(value.days) || Number(value.days) <= 0) {
                return this.$t('message.offsetDaysRequired');
            }
        }
        return true;
    }

    async mounted() {
        this.local.vendorTypeOptions = getEnumerationOptions('VendorType', Object.keys(VendorType), this);
        this.local.vendorOptions = await getVendorOptions();
        this.local.row = (await getResource<PurchaseInvoiceRowMapping>(this.resourceType, this.id))!!;

        this.local.periodizationModel = {
            type: this.local.row.invoicePeriodizationType,
            useOffset: this.local.row.invoicePeriodizationUseOffset ?? false,
            direction: this.local.row.invoicePeriodizationOffsetDirection ?? InvoicePeriodizationOffsetDirection.ADD,
            days: this.local.row.invoicePeriodizationOffsetDays ?? 0,
        };

        this.local.invoiceRowTypeOptions = await getDatabaseOptions('InvoiceRowType', this);
        this.local.vendorContractTypeOptions = getEnumerationOptions(
            'VendorContractType',
            Object.keys(VendorContractType),
            this,
        );
    }

    async save() {
        await this.saveResource();
    }

    async saveAndClose() {
        await this.saveResource(true);
    }

    async saveResource(goBack?: boolean): Promise<void> {
        try {
            await putResource(this.resourceType, this.id, this.local.row);
            successToast(this, 'message.purchaseInvoiceMappingSaved');

            if (goBack) {
                this.back();
            }
        } catch (error) {
            errorToast(this);
        }
    }

    back() {
        this.$router.go(-1);
    }
}
